import { ratePlayers } from 'api/rate';
import Button from 'components/Button';
import { Modal } from 'components/Modal';
import ScoresList from 'components/shared/ScoresList';
import Text from 'components/Text';
import useAlert from 'hooks/useAlert';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import useStores from 'stores/helpers/useStores';
import styled, { useTheme } from 'styled-components';
import { IPlayer } from 'types/Player';

const Tag = styled.button<{ hasRating?: boolean }>`
  border: none;
  outline: none;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${({ theme, hasRating }) =>
    hasRating ? theme.palette.dark[300] : theme.palette.primary[500]};
  color: ${({ theme }) => theme.palette.light[50]};
  cursor: pointer;
`;

const ModalContainer = styled.div`
  text-transform: initial;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 36px;
`;

interface Props {
  player: IPlayer;
}

export const RateTag: React.FC<Props> = observer(({ player }) => {
  const theme = useTheme();
  const alert = useAlert();
  const { playerStore } = useStores();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState<number>();

  if (!playerStore.players.length) {
    return null;
  }

  const playerRating = playerStore.players.find(
    (p) => p.id === player.id
  )?.userRating;

  const updateRating = async () => {
    if (!rating) {
      return;
    }

    setLoading(true);
    try {
      await ratePlayers({
        ratings: [
          {
            playerId: player.id,
            rating,
          },
        ],
      });
      alert.success('Player rating updated');
      playerStore.loadPlayers();
    } catch (e) {
      alert.error('Failed to update rating');
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Tag hasRating={!!playerRating} onClick={() => setIsOpen(true)}>
        <Text as="span" variant="custom" size={10}>
          {playerRating ? `Rating: ${playerRating}` : 'Rate'}
        </Text>
      </Tag>

      <ModalContainer>
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setRating(undefined);
          }}
        >
          <Text variant="h3" mb={12} color={theme.palette.dark[800]}>
            Your vote for {player.firstName} {player.lastName}
          </Text>
          <Text variant="p1" mb={24}>
            Select a number from 1 to 10 and tap "Update rating” to update your
            vote.
          </Text>

          <ScoresList
            selectedScore={rating ?? playerRating}
            onSelectScore={(r) => {
              setRating(r);
            }}
          />

          <ButtonContainer>
            <Button
              disabled={!rating || rating === player.userRating}
              onClick={updateRating}
              width="l"
              isLoading={loading}
            >
              Update rating
            </Button>
          </ButtonContainer>
        </Modal>
      </ModalContainer>
    </>
  );
});
